import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import z from 'zod'
import { AppLoader } from '~/components/loader'
import { useTitle } from '~/hooks'
import { useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import { Icon } from '@hap/components'

export const searchParamsSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
})

export const Route = createFileRoute(
  '/_authenticated/$alias/integrations/$integrationId/callback',
)({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
})

function RouteComponent() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const params = Route.useParams()
  const search = Route.useSearch()
  const trpc = useTRPC()
  const mutation = useMutation(trpc.integrations.set.mutationOptions())

  useTitle('Activate Integration')

  useEffect(() => {
    if (!search.state) setError('Invalid state key. Please try again.')
    else if (!search.code) setError('Invalid code provided. Please try again.')
    else
      mutation
        .mutateAsync({
          integrationId: params.integrationId,
          organizationIntegrationId: search.state,
          values: {
            config: {
              installed: true,
              code: search.code,
            },
          },
        })
        .catch((e) => {
          console.error(e)
          setError(
            'Failed to activate integration. Please reach out to support at help@happitu.com',
          )
        })
        .finally(() => {
          setLoading(false)
        })
  }, [])

  if (loading) return <AppLoader />
  if (error)
    return (
      <PublicTemplate width={400}>
        <div className="bg-accent text-center">
          <div className="flex flex-col content-center justify-center space-y-6 p-8 pb-16">
            <Icon
              name="warning-diamond"
              size="60"
              className="fill-earth-yellow mx-auto h-[100px]"
              weight="fill"
            />
            <p>{error}</p>
          </div>
        </div>
      </PublicTemplate>
    )

  return (
    <PublicTemplate>
      <div className="bg-accent space-y-10 px-8 py-10 text-center">
        <div className="flex justify-center gap-2">
          <div className="flex aspect-square h-[100px] shrink-0 items-center justify-center rounded-md bg-white p-4">
            <img src="https://www.mycase.com/favicon.png" className="w-fit" />
          </div>
        </div>

        <h1 className="text-center text-2xl font-semibold">Successfully Activated!</h1>
        <p>Happitu is now connected to MyCase. You may close this window.</p>
      </div>
    </PublicTemplate>
  )
}
